import { orderFetch, orderList, packOrderOpt, PackListParams } from "@/apis/packing";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams } from "@/types/common";
import { PackOrder, PackOrderStatus } from "@/types/packing";
import { Button, DatePicker, Flex, Input, notification, Select, TableProps, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import dayjs, { Dayjs } from "dayjs";
import { FC, Key, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import usePackEnums from "../hooks/usePackEnums";
import { numFormmater } from "@/pages/Requirement/util";
import { SConModal } from "@/styles/SConModal";
import AddPackOrder from "../components/AddPackOrder";
import PackDetail from "../components/PackDetail";
import { dateFormat } from "@/utils/format";
import PackingExportButton from "./PackingExportButton";

const { RangePicker } = DatePicker;

const PackingList: FC = () => {
  const { t } = useTranslation();
  // const { onOpen } = useContext<CustomTabPaneContextType<PackOrder>>(CustomTabPaneContext);
  const { getPackStatus, PackStatusEnums } = usePackEnums();
  const tableLayoutRef = useRef<TableLayoutRef | null>(null);
  const abordRef = useRef<AbortController>();
  const [list, total, loading, { fetchList, updateItem, addItem }] = useDataList<PackOrder, [CommonListParams<PackListParams>]>({ fetchApi: orderList });
  // const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg)

  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [number, setNumber] = useState<string>('');
  const [status, setStatus] = useState<PackOrderStatus | undefined>(undefined);
  const [detail, setDetail] = useState<PackOrder | null>(null);
  const [delData, setDelData] = useState<PackOrder | null>(null);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const mergeRequestRef = useRef(0);

  // 合并选中的订单，生成新的装箱单数据
  const mergeSelectedOrders = async () => {
    if (selectedRowKeys.length === 0) return;
    const requestId = ++mergeRequestRef.current;

    try {
      const orders = await Promise.all(selectedRowKeys.map(id => orderFetch(String(id))));
      if (requestId !== mergeRequestRef.current) return;
      return {
        number: orders.length === 1 ? orders[0].number : dayjs().format('YYYYMMDD_HHmmss'),
        boxes: orders.flatMap(order => order.boxes ?? []),
      };
    } catch (error) {
      if (requestId === mergeRequestRef.current) notification.error({ message: t('common.exportErr') });
    }
  };

  useEffect(() => {
    fetch(0);
    return () => {
      abordRef.current?.abort();
      mergeRequestRef.current += 1;
    };
  }, [])

  const fetch = (offset: number) => {
    if (offset === 0) tableLayoutRef.current?.resetTablePage();
    abordRef.current?.abort();
    const controller = new AbortController();
    abordRef.current = controller;
    fetchList({
      pagination: {
        offset,
        take: tableLayoutRef.current?.pageSize || DEFAULT_PAGE_SIZE,
      },
      body: {
        startTime: startTime ? startTime.startOf("day").valueOf() : undefined,
        endTime: endTime ? endTime.endOf("day").valueOf() : undefined,
        number: number || undefined,
        status,
        // orgId: curOrg.id
      },
      controller
    })
  };


  const handleDel = async (row: PackOrder) => {
    setDelData(row);
    try {
      await packOrderOpt(row.id, "void");
      notification.success({ message: t('warehouse.wasted') });
      updateItem({
        ...row,
        voidedAt: Date.now(),
        status: PackOrderStatus.WASTED
      })
    } catch (error) {
      notification.error({ message: t('common.operationFailed') })
    } finally { setDelData(null) }
  }
  const columns: TableProps<PackOrder>['columns'] = [
    {
      title: <SText text={t('warehouse.packingNo')} />,
      width: 180,
      key: "number",
      render: (_, record) => <Typography.Link onClick={() => setDetail(record)}>{record.number}</Typography.Link>
    },
    {
      title: <SText text={t('warehouse.refNo')} />,
      width: 160,
      key: "sourceOrderNumber",
      render: (_, record) => <SText text={record.sourceOrderNumber} />
    },
    {
      title: <SText text={t('warehouse.totalBoxes')} />,
      width: 120,
      key: "totalBoxes",
      render: (_, record) => <SText text={(record.totalBoxes || 0) + ''} />
    },
    {
      title: <SText text={t('warehouse.grossWeight')} />,
      width: 120,
      key: "totalGrossWeight",
      render: (_, record) => record.totalGrossWeight ? numFormmater(record.totalGrossWeight) : ''
    },
    {
      title: <SText text={t('warehouse.netWeight')} />,
      width: 120,
      key: "totalNetWeight",
      render: (_, record) => record.totalNetWeight ? numFormmater(record.totalNetWeight) : ''
    },
    {
      title: <SText text={t('warehouse.totalVolume')} />,
      width: 120,
      key: "totalVolume",
      render: (_, record) => record.totalVolume ? numFormmater(record.totalVolume) : ''
    },
    {
      title: <SText text={t('warehouse.packStatus')} />,
      width: 120,
      key: "packStatus",
      render: (_, record) => {
        let info = getPackStatus(record.status);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: <SText text={t('order.createdAt')} />,
      width: 120,
      key: "createdAt",
      render: (_, record) => <SText text={dateFormat(dayjs(record.createdAt))} />
    },
    {
      title: <SText text={t('common.opt')} />,
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Flex gap={6}>
          <Button type='link' onClick={() => setDetail(record)} >{t('common.detail')}</Button>
          {record.status === PackOrderStatus.DRAFT && <SConModal title={t('common.tip')} content={t('warehouse.delTip', { no: record.number })} onOk={() => handleDel(record)}>
            <Button type='link' danger loading={delData?.id === record.id} >{t('purchase.del')}</Button>
          </SConModal>}
        </Flex>
      )
    },
  ];

  const headerChildren = (
    <Flex style={{ flex: 1 }} justify='space-between' align='center'>
      <Flex gap={8} align='center' >
        <Button type='primary' onClick={() => setOpenAdd(true)} >{t('common.add')}</Button>
        <PackingExportButton
          onConfirm={mergeSelectedOrders}
          disabled={loading || selectedRowKeys.length === 0}
        />
      </Flex>
      <Flex align='center' gap={8}>
        <RangePicker
          size="small"
          onChange={val => {
            if (val) {
              setStartTime(val[0]);
              setEndTime(val[1]);
            } else {
              setStartTime(null);
              setEndTime(null);
            }
          }}
        />
        <Select
          options={PackStatusEnums}
          value={status}
          onChange={setStatus}
          placeholder={t('warehouse.packStatus')}
          style={{ width: 140 }}
          allowClear
        />
        <Input.Search
          value={number}
          onChange={e => setNumber(e.target.value)}
          placeholder={t('warehouse.packingNo')}
          enterButton onSearch={() => {
            setSelectedRowKeys([]);
            fetch(0);
          }}
        />
      </Flex>
    </Flex>
  )
  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        tableLayoutRef={tableLayoutRef}
        headerChildren={headerChildren}
        loading={loading}
        total={total}
        dataSource={list}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          preserveSelectedRowKeys: true,
          onChange: setSelectedRowKeys,
          getCheckboxProps: () => ({ disabled: loading }),
        }}
        handleChange={fetch}
        extHeight={40}
        columns={columns as Array<AnyObject>}
        scrollX={1000}
      />
      {openAdd && <AddPackOrder open={openAdd} onSuccess={order => {
        addItem(order)
        setOpenAdd(false)
      }} onClose={() => setOpenAdd(false)} />}
      {detail && <PackDetail detail={detail} open={!!detail} onClose={() => setDetail(null)} onConfirm={() => { setDetail(null) }} />}
    </Flex>
  );
};

export default PackingList;
